/** Top-level slash commands (not nested under a group). */
export const TOP_LEVEL_SLASH = new Set([
  "help",
  "settings",
  "website",
  "patron",
  "attune",
  "house",
  "houses",
  "customize",
  "partner",
  "bump-remind",
  "setup-colors",
  "ghost-ping",
  "setup-confessions",
  "todo",
  "test",
  "set-slevel",
  "host",
]);

/** Mirrors apps/bot/utils/slashGroups.js group membership. */
export const MOD_SLASH = new Set([
  "ban",
  "softban",
  "unban",
  "kick",
  "warn",
  "timeout",
  "jail",
  "unjail",
  "lock",
  "unlock",
  "slowmode",
  "nickname",
  "note",
  "reason",
  "purge",
  "history",
  "clearhistory",
  "modhistory",
  "move",
  "role",
  "modstats",
  "blockword",
  "lockdown",
]);

export const ADMIN_SLASH = new Set([
  "setup-verify",
  "setup-jail",
  "stream",
  "setup-roles",
  "refresh-roles",
  "edit-role-emojis",
  "import-roles",
  "setup-tickets",
  "setup-voice",
  "setmodlogs",
  "set-birthday",
  "set-levelup",
  "channels",
  "set-booster",
  "giveaway",
  "embed",
  "edit-embed",
  "edit-message",
  "create-message",
  "test",
  "quota",
  "quotas",
  "save",
  "autoreply",
]);

export const OWNER_SLASH = new Set([
  "give",
  "remove",
  "grant-badge",
  "revoke-badge",
  "grant-donor",
  "grant-host",
  "set-level",
  "reset",
  "announce",
  "shop-add",
  "shop-remove",
]);

export function formatCommandSlash(cmd, categoryId) {
  if (cmd.slash) return cmd.slash;
  const name = cmd.name;
  if (TOP_LEVEL_SLASH.has(name)) return `/${name}`;

  switch (categoryId) {
    case "leveling":
      return `/level ${name}`;
    case "games":
      return `/games ${name}`;
    case "music":
      return `/music ${name}`;
    case "fun":
      return `/fun ${name}`;
    case "utility":
      return `/util ${name}`;
    case "mod":
      if (name === "settings") return "/settings";
      if (MOD_SLASH.has(name)) return `/mod ${name}`;
      if (OWNER_SLASH.has(name)) return `/owner ${name}`;
      return `/admin ${name}`;
    default:
      return `/${name}`;
  }
}

export const COMMAND_CATEGORIES = [
  {
    id: "leveling",
    label: "Leveling & Garden",
    emoji: "☾",
    description: "Slash: /level <name> · Prefix: .<name> (e.g. .mkt lux, .lb global)",
    commands: [
      { name: "profile", desc: "View a member's level and records." },
      { name: "daily", desc: "Recharge to gather daily Essence." },
      { name: "quest", desc: "View your current daily trial." },
      { name: "claim", desc: "Collect the rewards for your completed trial." },
      {
        name: "pass",
        desc: "Nocturne Pass — seasonal free/premium rewards and daily/weekly/monthly challenges · .pass .np",
      },
      {
        name: "shop",
        desc: "Hubs: items · luxury (elixirs/charms/garden) · themes (art/solids/legendary) · titles (entry/distinguished/prestige) · cosmetics · perks · .mkt .mktc .mktleg .mktart",
      },
      {
        name: "tutorial",
        desc: "Learn XP, Essence, garden, shop, pets, and the Pass · .tutorial .guide",
      },
      { name: "inventory", desc: "View your collected items and Essence." },
      { name: "work", desc: "Hourly shift, meditate, rob, protect, or use a satchel item · includes /level work vulnerable (enable|disable|status) · .shift .meditate .rob .hustle" },
      { name: "balance", desc: "Check how much Essence you hold." },
      {
        name: "top",
        desc: "Leaderboards by level, essence, or pets · add scope:Global for all servers (.top global, /level top level scope:Global).",
      },
      { name: "level", desc: "View your level and progression · aliases: .lvl, .l" },
      { name: "explore", desc: "Venture into the untamed edges of Nocturne." },
      { name: "plant", desc: "Sow a seed in the fertile soil of Nocturne." },
      { name: "garden", desc: "Tend to your plants and check their growth." },
      { name: "harvest", desc: "Gather the fruits of your patience." },
      { name: "water", desc: "Use a Mist Flask to speed up your plant's growth." },
      {
        name: "pet",
        desc: "Manage companions · view / feed / play / care (Supporter+: max feed+play) / train / refresh / stables / ability / rename",
      },
      {
        name: "collect",
        desc: "Supporter+: claim ready daily / work / meditate / harvest / quest / pet care in one go · .collect .collect all .collect care .claimall",
      },
      { name: "titles", desc: "Equip a title from your collection." },
      { name: "colors", desc: "Equip your earned name hues." },
      { name: "themes", desc: "Switch between your owned profile themes." },
      {
        name: "themeview",
        desc: "Preview any profile theme on your card (shop, donor, Pass, luxury) · .tv .themeview",
      },
      {
        name: "booster",
        desc: "View perks or manage your custom booster role · /level booster view|role|looks|share|unshare|unequip|role-clear · .booster looks",
      },
      { name: "badges", desc: "View milestone badges and their perks." },
      { name: "perks", desc: "Manage your unlocked server perks." },
      { name: "gift", desc: "Share Essence or items with another member." },
      {
        name: "slevel",
        desc: "Check your server rank XP for this guild (separate from global /level).",
      },
    ],
  },
  {
    id: "games",
    label: "Games & Gambling",
    emoji: "✦",
    description: "Slash: /games <name> · Prefix: .<name> (e.g. .bj .pk .sl)",
    commands: [
      { name: "coinflip", desc: "Wager essence on a coin flip · amounts like 2k / 2,000 · .cf" },
      { name: "slots", desc: "Wager essence on the Reels · amounts like 2k / 2,000 · .sl" },
      {
        name: "poker",
        desc: "Five-card draw vs the house · hold cards then draw · .pk / .poker",
      },
      {
        name: "blackjack",
        desc: "Play blackjack against the dealer · Hit / Stand / Double · .bj",
      },
      {
        name: "dice",
        desc: "Bet on high (4–6) or low (1–3) · 1.8× payout on a win.",
      },
      {
        name: "roulette",
        desc: "Bet red/black, odd/even, or an exact number 0–36 · .roulette 100 red",
      },
      {
        name: "hilo",
        desc: "Guess whether the next card is higher or lower. Ties push.",
      },
      {
        name: "scratch",
        desc: "Reveal three symbols on an Essence scratch card.",
      },
    ],
  },
  {
    id: "music",
    label: "Music",
    emoji: "🎵",
    description: "Slash: /music <name> · Prefix: .<name>",
    commands: [
      { name: "play", desc: "Play a song in your voice channel · extra bots via MUSIC_BOT_TOKENS for two rooms at once." },
      { name: "pause", desc: "Pause the current track." },
      { name: "resume", desc: "Resume paused playback (.unpause)." },
      { name: "queue", desc: "View the tracks waiting in the queue." },
      { name: "skip", desc: "Let the current track fade for the next one." },
      { name: "stop", desc: "Return the room to its quiet state." },
      { name: "nowplaying", desc: "Show the current track · .np" },
      { name: "volume", desc: "Set playback volume 0–150 · .vol" },
      { name: "loop", desc: "Loop off, the current track, or the queue." },
      { name: "shuffle", desc: "Shuffle upcoming tracks." },
      { name: "remove", desc: "Remove a track from the upcoming queue by position." },
      { name: "clear", desc: "Clear upcoming tracks (keeps the current song)." },
      { name: "seek", desc: "Jump to a time in the current track." },
      { name: "lyrics", desc: "Look up lyrics for the current track or a search." },
      { name: "dj", desc: "Set a DJ role required to control playback." },
      { name: "playlist", desc: "Save, list, load, or delete personal playlists." },
    ],
  },
  {
    id: "fun",
    label: "Fun & Social",
    emoji: "✨",
    description: "Slash: /fun <command> · Prefix: .<command> (e.g. .qt for quote)",
    commands: [
      { name: "oracle", desc: "Ask a yes/no fortune question." },
      { name: "omen", desc: "Get your personal reading for today." },
      { name: "poll", desc: "Post a quick reaction poll." },
      { name: "wouldyourather", desc: "Post a would-you-rather poll." },
      { name: "rate", desc: "Rate something (or someone) out of 100." },
      { name: "choose", desc: "Pick randomly from a list of options." },
      { name: "flip", desc: "Flip a coin (no wager)." },
      { name: "cat", desc: "Post Jay's cat (Nocturne & test server only)." },
      { name: "emoji", desc: "Enlarge a custom emoji, or list server emoji/stickers if none is given." },
      { name: "avatar", desc: "Show a user's avatar." },
      { name: "remind", desc: "Set or manage reminders, including short timers · .timer 5m" },
      { name: "rps", desc: "Challenge someone to rock-paper-scissors." },
      { name: "battle", desc: "Challenge someone's active pet to a battle." },
      { name: "hug", desc: "Give someone a hug (includes a GIF)." },
      { name: "pat", desc: "Gently pat someone (includes a GIF)." },
      { name: "bonk", desc: "Bonk someone softly (includes a GIF)." },
      { name: "slap", desc: "Slap someone theatrically (includes a GIF)." },
      { name: "kill", desc: "Fake-kill someone with a theatrical GIF · .kill @user" },
      { name: "run", desc: "Run away with a GIF · optional target · .run / .run @user" },
      { name: "fart", desc: "Fart on someone with chaotic GIF energy · .fart @user" },
      { name: "stab", desc: "Theatrical stab GIF · .stab @user" },
      { name: "strangle", desc: "Cartoon strangle GIF · .strangle @user" },
      { name: "cpr", desc: "Hero CPR GIF · .cpr @user" },
      { name: "smash", desc: "Smash someone with a GIF · .smash @user" },
      { name: "throw", desc: "Yeet someone with a GIF · .throw @user" },
      { name: "burn", desc: "Theatrical burn GIF · .burn @user" },
      { name: "cry", desc: "Cry with a GIF · optional target · .cry / .cry @user" },
      { name: "smile", desc: "Smile with a GIF · optional target · .smile / .smile @user" },
      { name: "die", desc: "Dramatically die with a GIF · optional target · .die / .die @user" },
      {
        name: "random",
        desc: "Random generators · /fun random color|number|percentage|compatibility|gay|joke · shortcuts: .rng, .roll, .compat/.ship, .joke, .howgay, .rcolor, .rpct",
      },
      { name: "bond", desc: "Form or break a cosmetic bond · view includes how long you've been bonded." },
      {
        name: "quote",
        desc: "Turn a message into a quote image; @mentions show display names · automatically posts to the configured quotes channel (.qt).",
      },
      {
        name: "adoption",
        desc: "Adopt members into your family tree or view it.",
      },
      { name: "trivia", desc: "Answer a trivia question for Essence (streaks boost the payout)." },
      { name: "contest", desc: "View pet and garden contest leaderboards." },
      { name: "act", desc: "Kiss, cuddle, poke, bite, punch, roast, or compliment · .kiss .cuddle .poke .bite .punch .roast .compliment" },
      { name: "games", desc: "Hangman and conversation topics · .hangman .guess .topic" },
      {
        name: "patron",
        desc: "Supporter+ perks, cooldowns, digest, echo/glow, notes, custom commands · top-level /patron",
      },
    ],
  },
  {
    id: "utility",
    label: "Utility",
    emoji: "🧭",
    description: "Slash: /util <name> · Top-level: /help, /website, /patron, /attune · Prefix: .<name>",
    commands: [
      { name: "help", desc: "Browse commands, look up one, or view aliases (.help aliases)." },
      { name: "website", desc: "Open the Kami website and dashboard." },
      {
        name: "attune",
        desc: "Discover which of Nocturne's four Orders fits you · top-level /attune",
      },
      { name: "house", desc: "View your Order in Nocturne · top-level /house" },
      { name: "houses", desc: "Explore Nocturne's four Orders · top-level /houses" },
      {
        name: "customize",
        desc: "Personal command color (Supporter+) or Kami per-server avatar/banner (Server Host unlock; Manage Server) · /customize",
      },
      { name: "userinfo", desc: "Member record with join date, messages sent, level, Essence, XP, badges, and roles." },
      { name: "staff", desc: "List members who hold the dashboard staff roles." },
      { name: "serverinfo", desc: "Server stats including emoji and sticker counts." },
      {
        name: "serverstats",
        desc: "Server activity lookback card · messages, voice, and member growth (joins/leaves) for 1d / 7d / 30d",
      },
      { name: "botinfo", desc: "Displays the vitals of Kami." },
      {
        name: "generate-banner",
        desc: "Generates the official Nocturne banner image.",
      },
      { name: "define", desc: "Look up a word definition." },
      { name: "translate", desc: "Translate short text." },
      {
        name: "tts",
        desc: "Speak a message in your voice channel · .tts hello · up to 200 chars (500 with Server Host)",
      },
      { name: "roleinfo", desc: "Show info about a role." },
      { name: "channelinfo", desc: "Show info about a channel." },
      { name: "invite", desc: "Get the bot invite and Nocturne server link." },
      { name: "bump", desc: "Listing links plus a 2-hour Essence/XP claim after you vote. Streak lasts 24 hours (.bump)." },
      {
        name: "suggest",
        desc: "Submit a server suggestion.",
      },
      {
        name: "apply",
        desc: "Receive the server application form in your DMs (when enabled).",
      },
      { name: "goal", desc: "View or configure the server community goal." },
      { name: "seasonal", desc: "Browse limited-time seasonal shop items." },
      { name: "report", desc: "Report an issue to staff." },
      {
        name: "pings",
        desc: "View who mentioned you recently, including ghost pings.",
      },
      {
        name: "snipe",
        desc: "Show recently deleted or edited messages in this channel.",
      },
      { name: "afk", desc: "Set or clear your AFK status." },
      { name: "count", desc: "Show this server's counting progress and record." },
      {
        name: "timezone",
        desc: "Set or view your timezone · prefix: .tz / .timezone [@user].",
      },
      {
        name: "steal",
        desc: "Steal emoji(s), image/GIF, or sticker · bulk steals every emote in a message · .steal emote <message id|link>",
      },
      {
        name: "tools",
        desc: "Timer, birthday (view yours, view @user, or set month+day), color preview, first message, jump link, boosters, member count · .tools birthday @user · .tools timer",
      },
    ],
  },
  {
    id: "mod",
    label: "Moderation & Setup",
    emoji: "🛡️",
    description: "Slash: /mod, /admin, or /owner <name> · Prefix: .<name> (e.g. .ban)",
    commands: [
      { name: "settings", desc: "Open the Kami Control Console." },
      { name: "ban", desc: "Permanently ban a member from Nocturne." },
      { name: "softban", desc: "Softban a member (ban + purge recent messages)." },
      { name: "unban", desc: "Lift a ban and welcome a member back to Nocturne." },
      { name: "kick", desc: "Remove a member from Nocturne." },
      { name: "warn", desc: "Formally warn a member." },
      { name: "timeout", desc: "Temporarily silence a member · minutes 0 lifts the timeout." },
      { name: "jail", desc: "Confine a member to the jail channel." },
      { name: "unjail", desc: "Release a member from the jail channel." },
      { name: "lock", desc: "Lock this channel · optional minutes for auto-unlock." },
      { name: "unlock", desc: "Unlock this channel so members can send messages again." },
      { name: "lockdown", desc: "Lock or unlock all text channels · optional auto-unlock minutes." },
      { name: "slowmode", desc: "Set slowmode in seconds or use a preset (off/short/chat/raid/lockdown)." },
      { name: "nickname", desc: "Change or clear a nickname · dehoist option sinks hoisted names." },
      { name: "note", desc: "Add, list, search, or delete staff case notes." },
      { name: "reason", desc: "Edit the reason on a user's most recent warning." },
      { name: "purge", desc: "Delete recent messages in this channel · .purge or .purify - optional @user" },
      { name: "role", desc: "Add or remove one or more roles · .ra / .rm · roles separated by , | ; +" },
      { name: "move", desc: "Move a member to another voice channel." },
      {
        name: "modstats",
        desc: "View a staff member's moderation statistics.",
      },
      { name: "quota", desc: "Check your staff activity quota for this period." },
      { name: "quotas", desc: "View or reset staff activity quotas." },
      { name: "channels", desc: "View all channels and their descriptions." },
      { name: "save", desc: "Snapshot, list, download, or restore roles, channels, and permissions." },
      { name: "history", desc: "View warnings and case notes · optional case number lookup." },
      { name: "modhistory", desc: "View punishments a staff member has issued · target, type, reason, date." },
      { name: "clearhistory", desc: "Wipes all warning records for a user." },
      { name: "blockword", desc: "Add a word to the forbidden list." },
      {
        name: "autoreply",
        desc: "Auto-replies (e.g. .ad / .partnership) with role/user access · add / access / remove / list · .ar",
      },
      { name: "setmodlogs", desc: "Sets the channel where moderation actions are logged." },
      {
        name: "set-birthday",
        desc: "Set (or clear) the channel for birthday announcements · checked daily.",
      },
      {
        name: "set-levelup",
        desc: "Set (or clear) the channel for Crystal Growth / level-up messages.",
      },
      { name: "setup-verify", desc: "Post or update the verification panel (button or OAuth)." },
      { name: "setup-jail", desc: "Configure the jail role and channel." },
      {
        name: "setup-roles",
        desc: "Post a self-assign role panel · type the message, pick roles, optionally Pick Emojis, then post. Slash only.",
      },
      {
        name: "refresh-roles",
        desc: "Sync a role panel’s buttons/menu/reactions after renaming roles or editing the panel list with @mentions.",
      },
      {
        name: "edit-role-emojis",
        desc: "Sync roles from the current embed, then replace default role-panel emojis with custom emojis.",
      },
      {
        name: "import-roles",
        desc: "Bulk-create roles from a pasted list (one per line; first line = highest rank).",
      },
      {
        name: "setup-colors",
        desc: "Create every color-role shade and post reaction panels · pick the role they sit under · /setup-colors below:@role",
      },
      {
        name: "setup-tickets",
        desc: "Set up or update the Support Desk panel (claim + close buttons on new tickets).",
      },
      {
        name: "host",
        desc: "Server Host add-ons · /host stats · template · brand · schedule · digest · desk · purge · also editable in dashboard Settings → Server Host",
      },
      {
        name: "custom-command",
        desc: "Home guild: review Benefactor /patron custom trigger requests · list / approve / deny",
      },
      {
        name: "schedule",
        desc: "Create a Nocturne schedule event (home / special guilds) · slash only",
      },
      {
        name: "setup-confessions",
        desc: "Post the anonymous confessions panel · confessions stay in-channel with a staff log · /setup-confessions",
      },
      {
        name: "todo",
        desc: "Create a staff to-do on the board · /todo create · claim, complete, reopen via buttons · configure channel and roles in the dashboard",
      },
      {
        name: "setup-voice",
        desc: "Set up the voice interface panel + create lobby for personal temp VCs.",
      },
      {
        name: "sticky",
        desc: "Keep a message or full Architect embed at the bottom of a channel · set / remove / list",
      },
      {
        name: "set-slevel",
        desc: "Set a member's per-server rank level · Manage Server · .ssl / .setslevel",
      },
      { name: "persist-roles", desc: "Restore members' roles when they rejoin." },
      {
        name: "stream",
        desc: "Configure and send stream live announcements · /admin stream announce|add|remove|list|status|reset",
      },
      { name: "shop-add", desc: "Add a role to the server shop (Nocturne owner only)." },
      { name: "shop-remove", desc: "Remove a role from the server shop (Nocturne owner only)." },
      { name: "set-booster", desc: "Activate a server-wide XP multiplier (Nocturne only)." },
      { name: "partner", desc: "Manage server partnerships." },
      {
        name: "bump-remind",
        desc: "After a DISBOARD bump, wait (2h / 90m / 1d) then ping a role once · /bump-remind set @role 2h",
      },
      {
        name: "ghost-ping",
        desc: "Ghost-ping new members in affiliate channels, then delete the ping · /ghost-ping add #channel",
      },
      { name: "giveaway", desc: "Start, end, or reroll offerings. Customize the embed in the dashboard." },
      { name: "embed", desc: "Open the Embed Architect tool." },
      { name: "edit-embed", desc: "Edit a Kami embed by message ID or message link." },
      { name: "edit-message", desc: "Edit a plain Kami message by message ID or message link." },
      { name: "create-message", desc: "Compose and post a plain message (no embed) · slash only" },
      {
        name: "test",
        desc: "Preview auto-messages in this channel using your saved settings.",
      },
      {
        name: "announce",
        desc: "Bot owner only: fan-out Kami announcements to opted-in servers.",
      },
      { name: "grant-badge", desc: "Bot owner only: bestow a global badge upon a member." },
      { name: "revoke-badge", desc: "Bot owner only: strip a global badge from a member." },
      {
        name: "grant-donor",
        desc: "Bot owner only: grant Supporter/Patron/Benefactor after an offline payment (roles, announce, supporters wall).",
      },
      {
        name: "grant-host",
        desc: "Bot owner only: unlock Server Host for a guild after an offline payment or comp · /grant-host guild_id:",
      },
      {
        name: "set-level",
        desc: "Bot owner only: set a member's global Kami level · .setlevel / .setlvl / .slvl",
      },
      { name: "give", desc: "Bot owner only: give Essence." },
      { name: "remove", desc: "Bot owner only: revoke global resources or badges." },
      { name: "reset", desc: "Bot owner only: reset a user's global cooldown timers (daily, explore, hourglass, pet-ability, all)." },
    ],
  },
];
